import React, { useContext } from "react";
import useTranslation from "../../../hooks/useTranslation";
import { useProblematicDevicesManagement } from "../../../hooks/productManagement";
import FeaturePageLayout from "../../Shared/FeaturePageLayout/FeaturePageLayout";
import FeatureHeader from "../../Shared/FeaturePageLayout/FeatureHeader/FeatureHeader";
import FeatureTable from "../../Shared/FeaturePageLayout/FeatureTable/FeatureTable";
import FeaturePagination from "../../Shared/FeaturePageLayout/FeaturePagination/FeaturePagination";
import { faCaretDown } from "../../../helpers/icons/fontAwesomeIcons";
import UserContext from "../../../context/UserContext";
import USER_ROLES from "../../../constants/userRoles";

const ProblematicDevicesSection = () => {
  const { t } = useTranslation("productManagerPage");
  const { user } = useContext(UserContext);
  const isSuperAdmin = user && user.roles === USER_ROLES.SUPER_ADMIN;
  const {
    // State
    setSearchTerm,
    selectedCompany,
    setSelectedCompany,
    filteredProblematicDevices,
    recordsPerPage,
    setRecordsPerPage,
    currentPage,
    setCurrentPage,
    paginatedProblematicDevices,
    loadingProblematicDevices,
    companies,

    // Functions
    applyFilters,
  } = useProblematicDevicesManagement();

  // Create selectors array
  const selectors = isSuperAdmin
    ? [
        {
          items: companies,
          loading: false,
          selectedValue: selectedCompany,
          onChange: setSelectedCompany,
          valueField: "companyid",
          labelField: "companyname",
          placeholderKey: "company",
          icon: faCaretDown,
        },
      ]
    : [];

  // Define columns for the problematic devices table
  const problematicDevicesColumns = [
    {
      key: "baseid",
      label: t("baseid"),
      render: (row) => renderWithDefault(row.baseid),
    },
    {
      key: "probesampletime",
      label: t("probesampletime"),
      render: (row) => {
        const ms = row.probesampletime;
        if (ms === null) return "-";

        // Convert to minutes
        const minutes = ms / 60000;

        // Handle different time formats
        if (minutes === 1) return `1 ${t("minute")}`;
        if (minutes < 60) return `${minutes} ${t("minutes")}`;

        // Convert to hours for larger values
        const hours = minutes / 60;
        if (hours === 1) return `1 ${t("hour")}`;
        return `${hours}  ${t("hours")}`;
      },
    },
    {
      key: "logoutdate",
      label: t("logoutdate"),
      //render: (row) => renderWithDefault(row.logoutdate),
      render: (row) => {
        const date = row.logoutdate;
        if (date === null) return "-";
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString("HU", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
      }
    },
    {
    key: "modifyconfig",
    label: t("modifyconfig"),
    render: (row) => renderWithDefault(row.modifyconfig ? t("yes") : t("no")),
    },
    {
      key: "active",
      label: t("active"),
      render: (row) => renderWithDefault(row.active ? t("yes") : t("no")),
    },
    {
      key: "companyname",
      label: t("companyName"),
      render: (row) => renderWithDefault(row.companyname),
    },
    {
      key: "status",
      label: t("status"),
      // render: (row) => renderWithDefault(row.status),
      render: (row) => {
        if (row.status === "Disconnected") {
          return `${t("base_disconnected")}`;
        }
        if (row.status === "Never connected") {
          return `${t("base_never_connected")}`;
        }
      },
    },
  ];

  // Render helper function for null values
  const renderWithDefault = (value) => (value === null ? "-" : value);

  return (
    <>
      <FeaturePageLayout>
        <FeatureHeader
          t={t}
          title={t("problematic-devices")}
          search={setSearchTerm}
          selectors={selectors}
          showCompanySelector={true}
          selectedCompany={selectedCompany}
          onCompanyChange={setSelectedCompany}
          selectorUniqeClassname="product-manager-page__problematic-devices-company-selector"
          display={{
            count: recordsPerPage,
            onChange: setRecordsPerPage,
            options: [5, 10, 20, 50],
          }}
        />
        <FeatureTable
          t={t}
          data={paginatedProblematicDevices}
          columns={problematicDevicesColumns}
          loading={loadingProblematicDevices}
          tableType="problematic-devices"
          actions={{
            view: true,
            edit: false,
            delete: false,
            custom: [],
          }}
        />
      </FeaturePageLayout>
      {filteredProblematicDevices.length > 0 && (
        <div className="pagination-container">
          <FeaturePagination
            t={t}
            currentPage={currentPage}
            totalPages={Math.ceil(
              (filteredProblematicDevices?.length || 0) / recordsPerPage
            )}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </>
  );
};

export default ProblematicDevicesSection;
